import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { adminApi, cmsApi } from '../lib/api';
import { 
  FileText, Plus, ArrowLeft, Eye, Trash2, Save, X, Loader2, AlertCircle
} from 'lucide-react';
import { FadeIn, AnimatedCard } from '../components/ui/AnimatedCard';
import toast from 'react-hot-toast';

export function AdminCms() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingPage, setEditingPage] = useState<any>(null);
  const [formData, setFormData] = useState({
    slug: '',
    title: '',
    titleBn: '',
    content: '',
    contentBn: '',
    metaDescription: '',
    isPublished: true,
  });

  const { data: pages, isLoading } = useQuery({
    queryKey: ['admin-pages'],
    queryFn: async () => {
      const res = await cmsApi.getPages();
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => adminApi.createPage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pages'] });
      toast.success('Page created successfully');
      setShowForm(false);
      resetForm();
    },
    onError: () => toast.error('Failed to create page'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => adminApi.updatePage(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pages'] });
      toast.success('Page updated successfully');
      setShowForm(false);
      setEditingPage(null);
      resetForm();
    },
    onError: () => toast.error('Failed to update page'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => adminApi.deletePage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-pages'] });
      toast.success('Page deleted successfully');
    },
    onError: () => toast.error('Failed to delete page'),
  });

  const resetForm = () => {
    setFormData({
      slug: '',
      title: '',
      titleBn: '',
      content: '',
      contentBn: '',
      metaDescription: '',
      isPublished: true,
    });
  };

  const handleEdit = (page: any) => {
    setEditingPage(page);
    setFormData({
      slug: page.slug,
      title: page.title,
      titleBn: page.titleBn || '',
      content: page.content,
      contentBn: page.contentBn || '',
      metaDescription: page.metaDescription || '',
      isPublished: page.isPublished,
    });
    setShowForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPage) {
      updateMutation.mutate({ id: editingPage.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const allPages = pages || [];

  return (
    <div className="space-y-6">
      <FadeIn>
        <div className="flex items-center gap-4">
          <Link to="/admin" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">CMS Pages</h1>
            <p className="text-slate-500">Manage website content pages</p>
          </div>
        </div>
      </FadeIn>

      {!showForm ? (
        <>
          <FadeIn>
            <button
              onClick={() => { setShowForm(true); setEditingPage(null); resetForm(); }}
              className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add New Page
            </button>
          </FadeIn>

          <FadeIn>
            <AnimatedCard className="p-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
              ) : (
                <div className="space-y-3">
                  {allPages.map((page: any) => (
                    <motion.div
                      key={page.id}
                      layout
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-400" />
                          <div className="font-medium text-slate-900">{page.title}</div>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            page.isPublished ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {page.isPublished ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        <div className="text-sm text-slate-500 ml-8">/{page.slug}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/page/${page.slug}`}
                          target="_blank"
                          className="p-2 hover:bg-white rounded-lg transition-colors"
                          title="View page"
                        >
                          <Eye className="w-4 h-4 text-slate-400" />
                        </Link>
                        <button
                          onClick={() => handleEdit(page)}
                          className="px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this page?')) {
                              deleteMutation.mutate(page.id);
                            }
                          }}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                  {allPages.length === 0 && (
                    <div className="text-center py-12 text-slate-400">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>No CMS pages found</p>
                      <p className="text-sm mt-1">Create your first page to get started</p>
                    </div>
                  )}
                </div>
              )}
            </AnimatedCard>
          </FadeIn>
        </>
      ) : (
        <FadeIn>
          <AnimatedCard className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">
                {editingPage ? 'Edit Page' : 'Create New Page'}
              </h2>
              <button
                onClick={() => { setShowForm(false); setEditingPage(null); }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Slug (URL)</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    placeholder="about-us"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Page Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                    placeholder="About Us"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title (Bengali)</label>
                <input
                  type="text"
                  value={formData.titleBn}
                  onChange={(e) => setFormData({ ...formData, titleBn: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  placeholder="আমাদের সম্পর্কে"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Content (HTML supported)</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none min-h-[200px]"
                  placeholder="<p>Your page content here...</p>"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Content (Bengali)</label>
                <textarea
                  value={formData.contentBn}
                  onChange={(e) => setFormData({ ...formData, contentBn: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none min-h-[150px]"
                  placeholder="বাংলা বিষয়বস্তু..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Meta Description</label>
                <input
                  type="text"
                  value={formData.metaDescription}
                  onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none"
                  placeholder="Brief description for SEO"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isPublished"
                  checked={formData.isPublished}
                  onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
                  className="w-4 h-4 text-primary-600 rounded border-slate-300"
                />
                <label htmlFor="isPublished" className="text-sm text-slate-700">Publish page immediately</label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  <Save className="w-4 h-4" />
                  {editingPage ? 'Update Page' : 'Create Page'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingPage(null); }}
                  className="px-6 py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </AnimatedCard>
        </FadeIn>
      )}
    </div>
  );
}
